// Progression engine.
//
// Rules (from the product spec):
//  * Units inside a domain are strictly sequential. Unit N becomes available
//    only when units 1..N-1 are complete.
//  * A unit completes ONLY when its runtime reports validated completion —
//    opening or reading it never counts.
//  * A unit marked `implemented: false` is IMPLEMENTATION PENDING: it can never
//    be completed, therefore its domain can never complete, therefore the next
//    domain never unlocks. Placeholders cannot leak progress.
//  * Unlocks are sticky. Once a unit/domain has been legitimately unlocked it
//    stays accessible, so resetting Lab 04 does not re-lock Labs 05–07 and
//    resetting Domain 01 does not erase or re-lock Domain 02.
//  * Reset is scoped: a lab reset clears one unit; a domain reset clears only
//    that domain's units.

import { store } from './store.js';

const KEY = 'progress';
const blank = () => ({ units: {}, domains: {}, createdAt: Date.now() });
export const unitKey = (d, u) => `${d}.${u}`;

/**
 * @param curriculum
 * @param st      storage namespace (learner store, or the admin sandbox store)
 * @param opts.bypass  ADMIN TEST MODE: every implemented unit can be opened without
 *                     prerequisites. Only ever used with the admin sandbox store.
 */
export function createProgress(curriculum, st = store, opts = {}) {
  let bypass = !!opts.bypass;
  let rec = st.get(KEY) || blank();
  const save = () => st.set(KEY, rec);
  const domain = (id) => curriculum.domains.find((d) => d.id === id);
  const U = (d, u) => rec.units[unitKey(d, u)] || {};

  const api = {
    reload() { rec = st.get(KEY) || blank(); },

    store: st,
    get bypass() { return bypass; },
    setBypass(v) { bypass = !!v; },

    isDomainUnlocked(dId) {
      if (bypass) return true;
      const i = curriculum.domains.findIndex((d) => d.id === dId);
      if (i === 0) return true;
      if (rec.domains[dId]?.unlocked) return true;
      return api.isDomainComplete(curriculum.domains[i - 1].id);
    },

    isDomainComplete(dId) {
      const d = domain(dId);
      return d.units.length > 0 && d.units.every((u) => u.implemented && U(dId, u.id).status === 'complete');
    },

    /** 'pending' | 'locked' | 'available' | 'in-progress' | 'complete' */
    unitState(dId, uId) {
      const d = domain(dId);
      const idx = d.units.findIndex((u) => u.id === uId);
      const unit = d.units[idx];
      const r = U(dId, uId);
      if (!unit.implemented) return 'pending';
      if (r.status === 'complete') return 'complete';
      if (bypass) return st.get(`lab:${unitKey(dId, uId)}`) ? 'in-progress' : 'available';
      if (!api.isDomainUnlocked(dId)) return 'locked';
      const prereqsDone = d.units.slice(0, idx).every((p) => U(dId, p.id).status === 'complete');
      if (!prereqsDone && !r.unlocked) return 'locked';
      return st.get(`lab:${unitKey(dId, uId)}`) ? 'in-progress' : 'available';
    },

    canOpen(dId, uId) {
      const s = api.unitState(dId, uId);
      return s === 'available' || s === 'in-progress' || s === 'complete';
    },

    domainSummary(dId) {
      const d = domain(dId);
      const done = d.units.filter((u) => U(dId, u.id).status === 'complete').length;
      return {
        total: d.units.length, done, implemented: d.units.filter((u) => u.implemented).length,
        pending: d.units.filter((u) => !u.implemented).length,
        unlocked: api.isDomainUnlocked(dId), complete: api.isDomainComplete(dId),
        pct: d.units.length ? Math.round((done / d.units.length) * 100) : 0,
      };
    },

    /** Next unit the learner should work on in a domain (or null). */
    currentUnit(dId) {
      const d = domain(dId);
      return d.units.find((u) => api.unitState(dId, u.id) === 'available' || api.unitState(dId, u.id) === 'in-progress') || null;
    },

    /**
     * Called by the lab runtime after its validation passes. Refuses when the
     * unit is not open (prevents completing locked or pending units).
     */
    markComplete(dId, uId, meta = {}) {
      if (!api.canOpen(dId, uId)) return false;
      if (!domain(dId).units.find((u) => u.id === uId)?.implemented) return false;
      const k = unitKey(dId, uId);
      rec.units[k] = { ...rec.units[k], status: 'complete', unlocked: true, completedAt: Date.now(), attempts: (rec.units[k]?.attempts || 0) + 1, ...meta };
      // Sticky-unlock the next unit.
      const d = domain(dId);
      const idx = d.units.findIndex((u) => u.id === uId);
      const next = d.units[idx + 1];
      if (next) rec.units[unitKey(dId, next.id)] = { ...rec.units[unitKey(dId, next.id)], unlocked: true };
      // Domain complete → sticky-unlock the next domain.
      if (api.isDomainComplete(dId)) {
        rec.domains[dId] = { ...rec.domains[dId], completedAt: rec.domains[dId]?.completedAt || Date.now() };
        const di = curriculum.domains.findIndex((x) => x.id === dId);
        const nd = curriculum.domains[di + 1];
        if (nd) rec.domains[nd.id] = { ...rec.domains[nd.id], unlocked: true };
        else rec.phase2Unlocked = true; // final networking domain done → Phase 2
      }
      save();
      return true;
    },

    /** Reset ONE learning unit: its completion flag and its lab state. */
    resetUnit(dId, uId) {
      const k = unitKey(dId, uId);
      const wasUnlocked = !!rec.units[k]?.unlocked || rec.units[k]?.status === 'complete';
      rec.units[k] = wasUnlocked ? { unlocked: true } : {};
      st.remove(`lab:${k}`);
      st.remove(`labj:${k}`); // journal copy of a finished run (src/core/cloudSync.js)
      if (!api.isDomainComplete(dId) && rec.domains[dId]) delete rec.domains[dId].completedAt;
      save();
    },

    /** Reset every unit in ONE domain. Other domains are untouched. */
    resetDomain(dId) {
      const d = domain(dId);
      for (const u of d.units) {
        delete rec.units[unitKey(dId, u.id)];
        st.remove(`lab:${unitKey(dId, u.id)}`);
      }
      if (rec.domains[dId]) delete rec.domains[dId].completedAt;
      save();
    },

    /** Sandbox helper: sticky-unlock every implemented unit and every domain (admin store only). */
    unlockAll() {
      if (st === store) throw new Error('unlockAll is only available in the admin test sandbox');
      for (const d of curriculum.domains) {
        rec.domains[d.id] = { ...rec.domains[d.id], unlocked: true };
        for (const u of d.units) if (u.implemented) rec.units[unitKey(d.id, u.id)] = { ...rec.units[unitKey(d.id, u.id)], unlocked: true };
      }
      save();
    },
    /** Sandbox helper: remove every record and lab in this namespace (admin store only). */
    clearAll() {
      if (st === store) throw new Error('clearAll is only available in the admin test sandbox');
      for (const k of st.keys('')) st.remove(k);
      rec = blank();
      save();
    },

    phase2Unlocked() {
      return !!rec.phase2Unlocked;
    },

    unitRecord: (dId, uId) => ({ ...U(dId, uId) }),
    raw: () => JSON.parse(JSON.stringify(rec)),
  };
  return api;
}
